import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Gauge, ArrowRight, Phone, MessageCircle, ChevronRight } from "lucide-react";
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import Konfigurator from "@/components/Konfigurator";
import ExtraSolutions from "@/components/sections/ExtraSolutions";
import ContactSection from "@/components/sections/ContactSection";
import Reveal from "@/components/motion/Reveal";
import { SITE } from "@/data/site";

const STEPS = [
  { n: "01", t: "Fahrzeug wählen", d: "Marke, Modell und Motorisierung im Konfigurator auswählen." },
  { n: "02", t: "Potenzial sehen", d: "Sofort die möglichen Mehrwerte an Leistung & Drehmoment einsehen." },
  { n: "03", t: "Angebot erhalten", d: "Unverbindlich anfragen – wir melden uns mit deinem persönlichen Angebot." },
];

export default function KonfiguratorPage() {
  return (
    <Layout>
      <Seo
        title="Chiptuning-Konfigurator Cloppenburg | Leistung berechnen | KeyMaster KFZ"
        description="Berechne dein Leistungs-Plus: Chiptuning- & Softwareoptimierungs-Konfigurator für den Raum Cloppenburg. Marke, Modell & Motor wählen und Potenzial für Stage 1–3 sofort sehen. KeyMaster KFZ."
        path="/konfigurator"
        keywords={["Chiptuning Konfigurator Cloppenburg", "Leistung berechnen", "Stage 1 Stage 2 Stage 3", "Leistungssteigerung Rechner", "Softwareoptimierung Konfigurator"]}
      />

      {/* Hero */}
      <section className="relative overflow-hidden pt-32 pb-14 sm:pt-40">
        <div className="absolute inset-0 z-0 radial-glow" />
        <div className="relative z-10 mx-auto max-w-[1400px] px-5 sm:px-8">
          <nav className="mb-8 flex items-center gap-2 font-body text-xs text-neutral-500" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-volt">Start</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-neutral-300">Konfigurator</span>
          </nav>
          <Reveal>
            <div className="mb-6 flex h-16 w-16 items-center justify-center border border-volt/40 text-volt">
              <Gauge className="h-8 w-8" />
            </div>
            <p className="mb-3 font-body text-[11px] font-bold uppercase tracking-[0.3em] text-volt">Live-Konfigurator</p>
            <h1 className="max-w-4xl font-heading font-black uppercase leading-[0.88] tracking-tight text-white" style={{ fontSize: "clamp(2.5rem, 7vw, 6rem)" }}>
              Leistungs-<span className="text-volt">Konfigurator</span>
            </h1>
            <p className="mt-7 max-w-2xl font-body text-base leading-relaxed text-neutral-300 sm:text-lg">
              Finde in Sekunden heraus, welches Potenzial in deinem Fahrzeug steckt.
              Wähle Marke, Modell und Motor – für Chiptuning & Softwareoptimierung im Raum Cloppenburg.
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <a href={`tel:${SITE.phoneIntl}`} data-testid="konf-page-call" className="inline-flex items-center justify-center gap-2 bg-volt px-8 py-4 font-body text-sm font-bold uppercase tracking-wide text-black transition-transform hover:scale-[1.03] glow-volt">
                <Phone className="h-5 w-5" /> {SITE.phoneDisplay}
              </a>
              <a href={SITE.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="konf-page-whatsapp" className="inline-flex items-center justify-center gap-2 border border-white/20 px-8 py-4 font-body text-sm font-bold uppercase tracking-wide text-white transition-colors hover:border-volt hover:text-volt">
                <MessageCircle className="h-5 w-5" /> WhatsApp
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Configurator embed */}
      <section id="konfigurator" className="relative bg-[#050505] pb-20" data-testid="konfigurator-page-section">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <Reveal>
            <div className="border border-white/10 bg-[#0a0a0a] p-4 sm:p-8">
              <Konfigurator />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Steps */}
      <section className="bg-[#080808] py-20 sm:py-24">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="grid gap-4 md:grid-cols-3">
            {STEPS.map((s, i) => (
              <motion.div key={s.n}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.5, delay: i * 0.08 }}
                className="border border-white/10 bg-[#0d0d0d] p-7">
                <span className="font-heading text-4xl font-black text-volt">{s.n}</span>
                <h3 className="mt-3 font-heading text-xl font-bold uppercase text-white">{s.t}</h3>
                <p className="mt-2 font-body text-sm text-neutral-400">{s.d}</p>
              </motion.div>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/chiptuning-cloppenburg" data-testid="konf-to-chiptuning" className="group inline-flex items-center gap-2 font-heading text-lg font-bold uppercase text-white hover:text-volt transition-colors">
                Mehr zu Chiptuning <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <span className="text-neutral-700">/</span>
              <Link to="/softwareoptimierung-cloppenburg" data-testid="konf-to-software" className="group inline-flex items-center gap-2 font-heading text-lg font-bold uppercase text-white hover:text-volt transition-colors">
                Softwareoptimierung <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <ExtraSolutions />
      <ContactSection defaultService="Chiptuning" />
    </Layout>
  );
}
